import React, { useState, Fragment, useRef, useEffect, useCallback, useMemo } from 'react';
import { connect } from 'react-redux';
import { getSearchData } from '../../../../service/geoLocationService';
import browserUtils from '../../../../utils/browserUtils';
import { geolocationHandler } from '../../../../utils/geoLocationUtils';
import InfoStrip from '../../../InfoStrip';
import { CURR_LOCATION, FROM_MANUAL, GEO_LOCATION_STORAGE } from '../../constants';
import './CityPicker.css';

const CityPicker = ({
  value = '',
  handleChange,
  cities = [],
  showPermissionToastHandler,
  preselectedCityData,
  setModalMessage,
  setCurrentCityLocation,
  setNearByCitiesList,
  omnitureCapture,
}) => {
  const [query, setQuery] = useState(value);
  const inputEl = useRef(null);
  const [searchCityList, setSearchCityList] = useState([]);
  const [showLoader, setShowLoader] = useState(false);

  useEffect(() => {
    inputEl.current.focus();
  }, []);

  const handleSearchLocation = async val => {
    const searchedDataResponse = await getSearchData(val, preselectedCityData?.fromCityCode);
    const { data } = searchedDataResponse;
    const { success, searchDepatureCityList } = data;
    if (success) {
      setSearchCityList(searchDepatureCityList);
      setShowLoader(false);
    } else {
      setSearchCityList([]);
      setShowLoader(false);
    }
  };
  const debounceFun = useCallback(browserUtils.debouce(handleSearchLocation, 500), []);

  const handleQuery = value => {
    setQuery(value);
    debounceFun(value);
    setShowLoader(true);
  };

  const handleCurrentLocation = () => {
    omnitureCapture('Use_Current_location');
    const getCoordinates = geolocationHandler(() => {});
    if (getCoordinates) {
      getCoordinates.getCurrentPosition(
        async position => {
          if (position.coords) {
            const searchedDataResponse = await getSearchData(
              position.coords,
              preselectedCityData?.fromCityCode
            );
            const { data } = searchedDataResponse;
            const { success, searchDepatureCityList } = data;
            const {
              message = null,
              city: cityObj,
              nearByCities = null,
            } = searchDepatureCityList?.[0];
            if (success) {
              let city = null;
              if (cityObj) {
                city = { airportCode: null, ...cityObj };
              }
              setModalMessage(message);
              setCurrentCityLocation(city);
              setNearByCitiesList(nearByCities);
              if (city) {
                const { cityName, locusId = null, countryName, airportCode = null } = city;
                setSearchCityList(searchDepatureCityList);
                sessionStorage.setItem(GEO_LOCATION_STORAGE, JSON.stringify(city));
                handleChange(
                  {
                    name: cityName,
                    data: airportCode,
                    locusId,
                    country: countryName,
                    from: message !== null ? GEO_LOCATION_STORAGE : '',
                    omnitureFrom: CURR_LOCATION,
                    sectionName: 'current location',
                  },
                  false
                );
              }
            } else {
              showPermissionToastHandler();
            }
          }
        },
        error => {
          console.log('error ', error);
          showPermissionToastHandler();
        }
      );
    } else {
      showPermissionToastHandler();
    }
  };

  const nonAirport = useMemo(
    () =>
      searchCityList?.findIndex(cityList => {
        let airportCode = null;
        const { city, nearByCities } = cityList;
        if (city) {
          airportCode = city?.airportCode || null;
        }
        return nearByCities?.length > 0;
      }),
    [searchCityList, query]
  );

  return (
    <div className="citypicker_container">
      <input
        ref={inputEl}
        placeholder="Enter City"
        className="citypicker_input"
        type="text"
        onChange={event => handleQuery(event.target.value)}
        value={query}
      />
      <div className="citypicker_list">
        <div className="userLocation" onClick={() => handleCurrentLocation()}>
          <span className="modifySearchSprite userLocationIcon" />
          <p className="appendLeft10 blackText">Use Current Location</p>
        </div>
        {showLoader ? (
          <div className="chatLoaderWrap">
            <p className="chatLoaderOuter">
              <span className="chatLoaderInner" />
            </p>
          </div>
        ) : searchCityList && searchCityList.length === 0 ? (
          cities.map((section, index) => {
            const { cities = [] } = section;
            return (
              cities?.length > 0 && (
                <Fragment key={index}>
                  <div className="citypicker_title">{section.title}</div>
                  <ul id="fromCityPicker">
                    {cities.map((city, index1) => (
                      <li
                        key={`${index}_${index1}`}
                        onClick={() =>
                          handleChange(
                            {
                              ...city,
                              from: FROM_MANUAL,
                              omnitureFrom: FROM_MANUAL,
                              sectionName: 'top searches',
                              cityType: 'Airport',
                            },
                            true
                          )
                        }
                      >
                        {city.name}
                      </li>
                    ))}
                  </ul>
                </Fragment>
              )
            );
          })
        ) : (
          searchCityList?.map((searchCity, index2) => {
            const { city = {}, nearByCities = null } = searchCity;
            if (city) {
              const { cityName = '', countryName = '', locusId = '', airportCode = null } = city;
              return (
                <div key={`search${index2}`} className="searchedCity">
                  <ul>
                    <li
                      className="makeFlex column"
                      onClick={() =>
                        handleChange(
                          {
                            name: cityName,
                            data: airportCode,
                            locusId,
                            country: countryName,
                            from: FROM_MANUAL,
                            omnitureFrom: FROM_MANUAL,
                            sectionName: 'search',
                            cityType: nearByCities ? 'Non Airport' : 'Airport',
                          },
                          true
                        )
                      }
                    >
                      <p className="makeFlex hrtlCenter">
                        <span className="modifySearchSprite locationIcon" />
                        <span className="appendLeft8 blackText">
                          <b>{`${cityName}, `}</b>
                          {countryName}
                        </span>
                      </p>
                      {nonAirport === index2 && nearByCities && nearByCities.length > 0 && (
                        <span className="cityCount">{`${nearByCities.length} Airports near ${cityName}`}</span>
                      )}
                      {nonAirport === index2 && (
                        <InfoStrip
                          message="Choose an Airport City to see packages with Flight"
                          bgColorClass="cityChangeWarningContainer"
                          iconClass="cityChangeWarningIcon"
                          classes="citySearch"
                        />
                      )}
                    </li>
                    {nonAirport === index2 && (
                      <div className="airport">
                        {nearByCities &&
                          nearByCities.map((city, index) => {
                            const {
                              distance = {},
                              cityName,
                              locusId,
                              countryName,
                              airportCode = null,
                            } = city;
                            const { value, unit } = distance;
                            return (
                              <li
                                className="makeFlex column appendLeft10"
                                key={index}
                                onClick={() =>
                                  handleChange(
                                    {
                                      name: cityName,
                                      data: airportCode,
                                      locusId,
                                      country: countryName,
                                      from: FROM_MANUAL,
                                      omnitureFrom: FROM_MANUAL,
                                      sectionName: 'search',
                                      cityType: 'Airport',
                                    },
                                    true
                                  )
                                }
                              >
                                <span className="blackText">
                                  <b>{`${city.cityName}, `}</b>
                                  {countryName}
                                </span>
                                {Object.keys(distance).length > 0 && (
                                  <span className="font12 greyText appendTop3">{`${value} ${unit} from ${cityName}`}</span>
                                )}
                              </li>
                            );
                          })}
                      </div>
                    )}
                  </ul>
                </div>
              );
            }
          })
        )}
      </div>
    </div>
  );
};

const mapStateToProps = state => {
  const preselectedCityData = state?.search?.default;
  return {
    preselectedCityData,
  };
};

export default connect(mapStateToProps, null)(CityPicker);
